import { motion } from 'framer-motion';
import { useState } from 'react';
import {
  DollarSign,
  Building,
  TrendingUp,
  Cube,
  Globe,
  Beaker,
  Cpu,
  Truck,
  ShoppingBag,
  Sun,
  Heart,
  Wrench,
  Rocket,
  CircleDollarSign,
  Smartphone,
  Cloud,
  BarChart3,
  Target,
  Zap
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

function Portfolio() {
  const [selectedIndustry, setSelectedIndustry] = useState(null);

  const industries = [
    {
      id: 'tech',
      name: 'Technology',
      icon: Cpu,
      percentage: 25,
      description: 'Leading tech companies and emerging startups in AI, cloud computing, and cybersecurity.',
      companies: ['Artificial Intelligence', 'Cloud Services', 'Cybersecurity', '5G Networks'],
      color: 'blue',
      growth: '+18.5%'
    },
    {
      id: 'finance',
      name: 'Financial Services',
      icon: DollarSign,
      percentage: 20,
      description: 'Digital payment solutions, fintech innovations, and blockchain technology.',
      companies: ['Digital Banking', 'Payment Processing', 'Blockchain', 'Insurance Tech'],
      color: 'green',
      growth: '+15.2%'
    },
    {
      id: 'realestate',
      name: 'Real Estate',
      icon: Building,
      percentage: 15,
      description: 'Premium commercial properties, residential developments, and REITs.',
      companies: ['Commercial Properties', 'Residential Developments', 'Data Centers', 'Industrial Facilities'],
      color: 'purple',
      growth: '+12.8%'
    },
    {
      id: 'healthcare',
      name: 'Healthcare',
      icon: Heart,
      percentage: 10,
      description: 'Biotechnology, medical devices, and healthcare technology solutions.',
      companies: ['Biotech Research', 'Medical Devices', 'Digital Health', 'Pharmaceuticals'],
      color: 'red',
      growth: '+22.1%'
    },
    {
      id: 'energy',
      name: 'Clean Energy',
      icon: Sun,
      percentage: 10,
      description: 'Renewable energy projects, sustainable technologies, and clean energy infrastructure.',
      companies: ['Solar Power', 'Wind Energy', 'Energy Storage', 'Smart Grid'],
      color: 'yellow',
      growth: '+28.7%'
    },
    {
      id: 'manufacturing',
      name: 'Advanced Manufacturing',
      icon: Wrench,
      percentage: 5,
      description: '3D printing, robotics, and smart manufacturing solutions.',
      companies: ['3D Printing', 'Robotics', 'IoT Manufacturing', 'Smart Factories'],
      color: 'cyan',
      growth: '+16.3%'
    },
    {
      id: 'ecommerce',
      name: 'E-Commerce',
      icon: ShoppingBag,
      percentage: 5,
      description: 'Digital retail platforms, logistics, and e-commerce technology.',
      companies: ['Online Retail', 'Digital Marketplaces', 'Logistics Tech', 'Payment Solutions'],
      color: 'pink',
      growth: '+19.4%'
    },
    {
      id: 'logistics',
      name: 'Transportation & Logistics',
      icon: Truck,
      percentage: 5,
      description: 'Electric vehicles, autonomous transportation, and smart logistics.',
      companies: ['Electric Vehicles', 'Autonomous Tech', 'Smart Logistics', 'Fleet Management'],
      color: 'indigo',
      growth: '+14.6%'
    },
    {
      id: 'crypto',
      name: 'Cryptocurrency',
      icon: CircleDollarSign,
      percentage: 5,
      description: 'Digital assets, DeFi protocols, and blockchain infrastructure.',
      companies: ['Bitcoin', 'Ethereum', 'DeFi Protocols', 'NFT Platforms'],
      color: 'orange',
      growth: '+35.2%'
    }
  ];

  const portfolioStats = [
    { value: '$2.8B', label: 'Total Portfolio Value', icon: DollarSign },
    { value: '94%', label: 'Success Rate', icon: Target },
    { value: '+24.5%', label: 'Average Annual Return', icon: TrendingUp },
    { value: '150+', label: 'Active Investments', icon: Building }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <Navigation />

      {/* Hero Section - Unique Design */}
      <section className="relative pt-8 md:pt-20 pb-16 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 right-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 right-1/4 w-24 h-24 bg-cyan-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto py-20">
            <motion.div
              className="inline-block mb-6 px-6 py-3 rounded-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-400 font-semibold text-sm border border-blue-500/30"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              📊 Strategic Investment Portfolio
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-6xl font-bold mb-8 leading-tight"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Our Investment
              </span>
              <br />
              <span className="text-white">
                Portfolio
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-xl mb-12 text-gray-300 leading-relaxed max-w-3xl mx-auto"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Diversified investments across high-growth industries, delivering consistent returns through strategic asset allocation and expert management.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <Link to="/investment-plans">
                <button className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 rounded-xl text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-2xl">
                  Explore Investments
                </button>
              </Link>
              <button className="border-2 border-white/30 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white/10 transition-all duration-300">
                View Performance
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Portfolio Stats */}
      <section className="py-20 bg-gradient-to-r from-slate-800 to-slate-700">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {portfolioStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  className="text-center p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.5 }}
                >
                  <Icon className="mx-auto mb-4 text-blue-400" size={48} />
                  <div className="text-3xl font-bold mb-2">{stat.value}</div>
                  <div className="text-gray-400">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industry Allocation */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Industry Allocation
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Strategic distribution of assets across sectors positioned for long-term growth and stability
            </p>
          </div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {industries.map((industry) => {
              const Icon = industry.icon;
              return (
              <motion.div
                key={industry.id}
                  className="card-glass p-8 rounded-2xl card-hover cursor-pointer"
                variants={itemVariants}
                whileHover={{ y: -5, scale: 1.02 }}
                onClick={() => setSelectedIndustry(industry)}
              >
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-12 h-12 bg-gradient-to-r from-${industry.color}-600 to-${industry.color}-500 rounded-xl flex items-center justify-center`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-white">{industry.percentage}%</div>
                      <div className="text-sm text-green-400">{industry.growth}</div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">{industry.name}</h3>
                  <p className="text-gray-300 mb-6 leading-relaxed">{industry.description}</p>

                  <div className="space-y-2">
                    {industry.companies.slice(0, 3).map((company, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                        <span className="text-sm text-gray-400">{company}</span>
                      </div>
                    ))}
                    {industry.companies.length > 3 && (
                      <div className="text-sm text-blue-400 font-medium">
                        +{industry.companies.length - 3} more
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Investment Strategy */}
      <section className="py-20 bg-gradient-to-r from-slate-700 to-slate-800">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Our Investment Strategy
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                We employ a sophisticated multi-strategy approach that combines fundamental analysis, technical insights, and market timing to maximize returns while managing risk.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    01
                    </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Diversification</h3>
                    <p className="text-gray-300">Spread investments across multiple sectors and asset classes to reduce risk.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    02
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Active Management</h3>
                    <p className="text-gray-300">Continuous monitoring and adjustment based on market conditions and opportunities.</p>
          </div>
        </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    03
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Risk Management</h3>
                    <p className="text-gray-300">Comprehensive risk assessment and mitigation strategies for capital preservation.</p>
        </div>
          </div>
          </div>
        </div>

            <div className="card-glass p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-white mb-6">Performance Highlights</h3>
              
              <div className="space-y-6">
                <div className="flex justify-between items-center p-4 bg-white/5 rounded-xl">
                  <div>
                    <div className="text-white font-semibold">Total Return</div>
                    <div className="text-gray-400 text-sm">Last 12 months</div>
                  </div>
                  <div className="text-2xl font-bold text-green-400">+24.5%</div>
                </div>

                <div className="flex justify-between items-center p-4 bg-white/5 rounded-xl">
                  <div>
                    <div className="text-white font-semibold">Sharpe Ratio</div>
                    <div className="text-gray-400 text-sm">Risk-adjusted return</div>
                  </div>
                  <div className="text-2xl font-bold text-blue-400">1.85</div>
                </div>

                <div className="flex justify-between items-center p-4 bg-white/5 rounded-xl">
                  <div>
                    <div className="text-white font-semibold">Max Drawdown</div>
                    <div className="text-gray-400 text-sm">Peak to trough</div>
                  </div>
                  <div className="text-2xl font-bold text-red-400">-8.2%</div>
                </div>

                <div className="flex justify-between items-center p-4 bg-white/5 rounded-xl">
                  <div>
                    <div className="text-white font-semibold">Win Rate</div>
                    <div className="text-gray-400 text-sm">Successful trades</div>
          </div>
                  <div className="text-2xl font-bold text-purple-400">94%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Invest in Our Portfolio?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of investors who trust our proven investment strategies and portfolio management expertise.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/investment-plans">
              <button className="bg-white text-slate-900 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-2xl">
                Start Investing Now
              </button>
            </Link>
              <Link to="/contact">
              <button className="border-2 border-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white/10 transition-all duration-300">
                Contact Our Team
                </button>
              </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Portfolio;